import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import PaymentsTab from "../../pages/Contract/ContractModal/PaymentsTab";
import { paymentValidationSchema } from '../../pages/Contract/ContractModal/hooks/validationSchemas';
import moment from "moment";

const EditPaymentModal = ({
  isOpen,
  toggle,
  editForm,
  setEditForm,
  onSave,
}) => {
  const payments = editForm?.payments || [];
  const unitInfo = editForm?.unitInfo || null;
  const contract = editForm?.contract || null;

  const formik = useFormik({
    initialValues: {
      units: [],
      balance: 0,
      booking_date: "",
      amount: 0,
      settlement_price: editForm?.settlement_price || 0,
      settled_price: editForm?.settled_price || editForm?.settlement_price || 0,
      extra_payments: 0,
      payments: [],
      remaining_balance: 0,
      vat_percentage: editForm?.vat_percentage || 19,
      isSettlementPriceManual: editForm?.isSettlementPriceManual || false
    },
    validationSchema: paymentValidationSchema,
    onSubmit: (values) => {
      const processedPayments = values.payments.map(payment => {
        return {
          ...payment,
        };
      });
      const finalPrice = values.settled_price;
      
      onSave(
        processedPayments, 
        finalPrice,
        unitInfo
      );
      
      toggle();
    },
  });

  useEffect(() => {
    if (isOpen && payments) {
      const validPayments = Array.isArray(payments) ? payments : [];
      
      const settlementPrice = editForm?.settlement_price 
        ? parseFloat(editForm.settlement_price) 
        : Math.max(
            ...validPayments.map(p => 
              p.settlement_price ? parseFloat(p.settlement_price) : 0
            ), 
            0
          );
      
      const finalSettlementPrice = isNaN(settlementPrice) || settlementPrice < 0 ? 0 : settlementPrice;
      
      const unitsMap = new Map();
      
      if (contract && contract.units) {
        contract.units.forEach((unit) => {
          unitsMap.set(unit.id, {
            unitId: unit.id,
            unit_nr: unit.nr || "",
            unit_type: unit.type || "Standard",
            settled_price: 0,
            extras: unit.extras || 0,
            showExtras: !!unit.extras
          });
        });
      }
      else if (unitInfo) {
        unitsMap.set(unitInfo.id, {
          unitId: unitInfo.id,
          unit_nr: unitInfo.nr || "",
          unit_type: unitInfo.unit_type || "Standard",
          settled_price: finalSettlementPrice,
          extras: unitInfo.extras || 0,
          showExtras: !!unitInfo.extras
        });
      }
      else if (unitsMap.size === 0) {
        unitsMap.set("1", {
          unitId: "1",
          unit_nr: "1",
          unit_type: "Standard",
          settled_price: finalSettlementPrice,
          extras: 0,
          showExtras: false
        });
      }
      
      const formattedPayments = validPayments.map(payment => {
        const unitAmounts = payment.unitAmounts || [];

        if (unitAmounts.length === 0 && payment.unit_id) {
          const unitId = payment.unit_id;
          const unit = unitsMap.get(unitId);
          
          if (unit) {
            unitAmounts.push({
              unit_id: unitId,
              unit_nr: unit.unit_nr,
              unit_type: unit.unit_type,
              amount: String(payment.amount || 0)
            });
          }
        }
        
        if (unitAmounts.length === 0 && unitsMap.size > 0) {
          const firstUnit = unitsMap.values().next().value;
          unitAmounts.push({
            unit_id: firstUnit.unitId,
            unit_nr: firstUnit.unit_nr,
            unit_type: firstUnit.unit_type,
            amount: String(payment.amount || 0)
          });
        }
        
        const processedUnitAmounts = unitAmounts.map(ua => ({
          unit_id: ua.unit_id || "1",
          unit_nr: ua.unit_nr || unitsMap.get(ua.unit_id)?.unit_nr || "1",
          unit_type: ua.unit_type || unitsMap.get(ua.unit_id)?.unit_type || "Standard",
          amount: String(ua.amount || 0)
        }));
        
        const bookingDate = payment.booking_date ? 
          moment(payment.booking_date).format("YYYY-MM-DD") : "";
        
        return {
          id: payment.id,
          type: payment.type || "",
          amount: payment.amount || 0,
          booking_date: bookingDate,
          isPaid: payment.cashed? true : false,
          cashed: payment.cashed,
          cashed_date: payment.cashed_date,
          vat_percentage: parseInt(payment.vat_percentage || 19),
          unitAmounts: processedUnitAmounts,
          unit_id: payment.unit_id
        };
      });
      
      const unitSettlements = new Map();
      formattedPayments.forEach(payment => {
        if (payment.unitAmounts && payment.unitAmounts.length > 0) {
          payment.unitAmounts.forEach(unitAmount => {
            const unitId = unitAmount.unit_id;
            const amount = parseFloat(unitAmount.amount) || 0;
            
            if (!unitSettlements.has(unitId)) {
              unitSettlements.set(unitId, amount);
            } else {
              unitSettlements.set(unitId, unitSettlements.get(unitId) + amount);
            }
          });
        }
      });
      
      unitSettlements.forEach((amount, unitId) => {
        if (unitsMap.has(unitId)) {
          unitsMap.get(unitId).settled_price = amount;
        }
      });
      
      const unitsArray = Array.from(unitsMap.values());
      
      formik.setValues({
        ...formik.values,
        units: unitsArray,
        settlement_price: finalSettlementPrice,
        settled_price: finalSettlementPrice,
        payments: formattedPayments,
        vat_percentage: validPayments.length > 0 ? 
          parseInt(validPayments[0]?.vat_percentage || 19) : 19
      });
    }
  }, [isOpen, payments, editForm, unitInfo, contract]);
  const modalTitle = unitInfo 
    ? `Manage Payments - Unit ${unitInfo.nr} (${unitInfo.unit_type})` 
    : contract 
    ? `Manage Payments - Contract ${contract.pvc_number}`
    : "Manage Payments";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
      <ModalBody>
        <Form>
          <PaymentsTab formik={formik} />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
        <Button color="primary" onClick={formik.handleSubmit}>
          Save All Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditPaymentModal;